import parameterize from 'parameterize';
import rehypeParse from 'rehype-parse';
import rehypeStringify from 'rehype-stringify';
import { unified } from 'unified';
import {
  CUSTOM_SECTION_TYPE,
  GROUP_CHRONOLOGY_SECTION_TYPE,
} from '../consts/content-types';
import { snakeToTitle } from './caseconverters';
import {
  clearHeadingFromChildrenTags,
  createTOC,
  makeYearHeading,
} from './rehype';

export const sectionIsNotEmpty = (value) =>
  Array.isArray(value) &&
  value.some((child) => !!child.value && child.value !== '\n');

export const parseContent = (data, allowYearHeading = false) => {
  let toc = [];
  const contents = data?.map((el) => {
    const { type, value, id } = el;
    const isGroupChronologySection = type === GROUP_CHRONOLOGY_SECTION_TYPE;
    const shouldApplyYearHeading = allowYearHeading && isGroupChronologySection;

    if (type === 'images') {
      return el;
    }
    // Normalize `value` into an array for consistent rendering
    const normalizedValues = Array.isArray(value) ? value : [{ ...el }];
    if (type === CUSTOM_SECTION_TYPE) {
      if (sectionIsNotEmpty(value?.body)) {
        toc.push({
          id: parameterize(value.header),
          key: id,
          title: value.header,
          level: 0,
        });
      }
    } else if (sectionIsNotEmpty(normalizedValues)) {
      toc.push({ id: type, key: id, title: snakeToTitle(type), level: 0 });
    }

    // parse sections
    if (Array.isArray(value)) {
      // subsection
      const updatedValue = value.map((subsection) => {
        if (subsection.value?.content) {
          toc.push({
            id: parameterize(subsection.value.header),
            key: subsection.id,
            title: subsection.value.header,
            level: 1,
          });

          return {
            ...subsection,
          };
        }

        const content = unified()
          .use(rehypeParse, { fragment: true })
          .use(shouldApplyYearHeading ? makeYearHeading() : () => {})
          .use(
            createTOC({
              setTOC: (value) => {
                toc = [...toc, ...value];
              },
              tocIndex: toc.length,
            }),
          )
          .use(clearHeadingFromChildrenTags('h2'))
          .use(rehypeStringify)
          .processSync(
            subsection.content || subsection.value || subsection.description,
          )
          .toString();

        return {
          ...subsection,
          value: content,
        };
      });

      return {
        ...el,
        value: [...updatedValue],
      };
    }

    // Custom section
    if (Array.isArray(value?.body)) {
      const bodyUpdated = value?.body.map((subsection) => {
        if (!subsection.value) {
          return subsection;
        }

        if (subsection.value.header) {
          toc.push({
            id: parameterize(subsection.value.header),
            key: subsection.id,
            title: subsection.value.header,
            level: 1,
          });
        }

        if (subsection.type === 'content') {
          const content = unified()
            .use(rehypeParse, { fragment: true })
            .use(shouldApplyYearHeading ? makeYearHeading() : () => {})
            .use(
              createTOC({
                setTOC: (value) => {
                  toc = [...toc, ...value];
                },
                headingTagNames: ['h3'],
                tocIndex: toc.length,
              }),
            )
            .use(rehypeStringify)
            .processSync(subsection.value)
            .toString();

          if (typeof subsection.value === 'string') {
            return {
              ...subsection,
              value: content,
            };
          }
        }

        return subsection;
      });

      return {
        ...el,
        value: {
          ...el.value,
          body: bodyUpdated,
        },
      };
    }

    // section in MO
    if (typeof value?.content === 'string') {
      const content = unified()
        .use(rehypeParse, { fragment: true })
        .use(shouldApplyYearHeading ? makeYearHeading() : () => {})
        .use(
          createTOC({
            setTOC: (value) => {
              toc = [...toc, ...value];
            },
            tocIndex: toc.length,
          }),
        )
        .use(rehypeStringify)
        .processSync(value.content)
        .toString();

      return {
        ...el,
        value: {
          ...el.value,
          content,
        },
      };
    }

    return el;
  });

  return {
    toc,
    contents,
  };
};
